import { useEffect, useState } from "react";
import api from "@/lib/apiClient";
import { toast } from "sonner";
import { PAYMENT_MODES, fmtMoney } from "@/lib/paymentConstants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Check, ChevronsUpDown, Upload } from "lucide-react";

const EMPTY = {
  student_id: "", payment_date: new Date().toISOString().slice(0, 10), amount_received: "",
  payment_mode: "Cash", transaction_id: "", bank_name: "", received_by: "", remarks: "",
  next_due_amount: "", next_due_date: "",
};

export default function PaymentFormDialog({ open, onOpenChange, payment, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [students, setStudents] = useState([]);
  const [selected, setSelected] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [file, setFile] = useState(null);
  const isEdit = Boolean(payment?.id);

  useEffect(() => {
    if (!open) return;
    api.get("/students", { params: { page: 1, page_size: 100 } }).then((r) => setStudents(r.data.items)).catch(() => setStudents([]));
    if (payment?.id) {
      setForm({ ...EMPTY, ...payment, amount_received: payment.amount_received ?? "" });
      setSelected({ id: payment.student_id, student_code: payment.student_code, full_name: payment.student_name, course_enrolled: payment.course, batch: payment.batch });
    } else {
      setForm(EMPTY); setSelected(null); setFile(null);
    }
  }, [open, payment]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pick = (s) => {
    setSelected(s);
    set("student_id", s.id);
    setPickerOpen(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.student_id) { toast.error("Please select a student"); return; }
    if (!(parseFloat(form.amount_received) > 0)) { toast.error("Amount must be greater than 0"); return; }
    setSaving(true);
    try {
      const payload = {
        student_id: form.student_id, payment_date: form.payment_date,
        amount_received: parseFloat(form.amount_received), payment_mode: form.payment_mode,
        transaction_id: form.transaction_id, bank_name: form.bank_name, received_by: form.received_by,
        remarks: form.remarks, next_due_amount: parseFloat(form.next_due_amount) || 0, next_due_date: form.next_due_date,
      };
      let paymentId = payment?.id;
      if (isEdit) {
        await api.put(`/payments/${payment.id}`, payload);
      } else {
        const { data } = await api.post("/payments", payload);
        paymentId = data.payment.id;
        if (data.student_status === "Paid") toast.success("Balance cleared — student marked as Paid! 🎉");
      }
      if (file && paymentId) {
        const fd = new FormData(); fd.append("file", file);
        await api.post(`/payments/${paymentId}/screenshot`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(isEdit ? "Payment updated" : "Payment recorded");
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save payment");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto" data-testid="payment-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Payment" : "Add Payment"}</DialogTitle>
          <DialogDescription>Record an installment against a student's fee.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Student *</Label>
            <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
              <PopoverTrigger asChild>
                <Button type="button" variant="outline" role="combobox" disabled={isEdit} className="w-full justify-between font-normal" data-testid="payment-student-picker">
                  {selected ? `${selected.student_code} — ${selected.full_name}` : "Select a student..."}
                  <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search students..." />
                  <CommandList>
                    <CommandEmpty>No students found.</CommandEmpty>
                    <CommandGroup>
                      {students.map((s) => (
                        <CommandItem key={s.id} value={`${s.student_code} ${s.full_name}`} onSelect={() => pick(s)} data-testid={`student-option-${s.id}`}>
                          <Check className={`mr-2 h-4 w-4 ${selected?.id === s.id ? "opacity-100" : "opacity-0"}`} />
                          <span className="font-mono text-xs text-slate-400 mr-2">{s.student_code}</span>{s.full_name}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          {selected && (
            <div className="grid grid-cols-3 gap-3 rounded-lg bg-slate-50 p-3 text-sm">
              <div><p className="text-xs text-muted-foreground">Course</p><p className="text-secondary">{selected.course_enrolled || "—"}</p></div>
              <div><p className="text-xs text-muted-foreground">Batch</p><p className="text-secondary">{selected.batch || "—"}</p></div>
              <div><p className="text-xs text-muted-foreground">Pending</p><p className="font-medium text-rose-600">{fmtMoney(selected.pending_amount)}</p></div>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Amount Received (₹) *</Label>
              <Input type="number" min="1" value={form.amount_received} onChange={(e) => set("amount_received", e.target.value)} data-testid="payment-amount-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Payment Date *</Label>
              <Input type="date" value={form.payment_date} onChange={(e) => set("payment_date", e.target.value)} data-testid="payment-date-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Payment Mode</Label>
              <Select value={form.payment_mode} onValueChange={(v) => set("payment_mode", v)}>
                <SelectTrigger data-testid="payment-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>{PAYMENT_MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Transaction ID</Label>
              <Input value={form.transaction_id} onChange={(e) => set("transaction_id", e.target.value)} data-testid="payment-txn-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Bank Name</Label>
              <Input value={form.bank_name} onChange={(e) => set("bank_name", e.target.value)} data-testid="payment-bank-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Received By</Label>
              <Input value={form.received_by} onChange={(e) => set("received_by", e.target.value)} placeholder="Defaults to you" data-testid="payment-receivedby-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Next Due Amount (₹)</Label>
              <Input type="number" value={form.next_due_amount} onChange={(e) => set("next_due_amount", e.target.value)} data-testid="payment-nextdue-amount-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Next Due Date</Label>
              <Input type="date" value={form.next_due_date} onChange={(e) => set("next_due_date", e.target.value)} data-testid="payment-nextdue-date-input" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Remarks</Label>
            <Textarea rows={2} value={form.remarks} onChange={(e) => set("remarks", e.target.value)} data-testid="payment-remarks-input" />
          </div>

          {!isEdit && (
            <div className="space-y-1.5">
              <Label>Payment Screenshot</Label>
              <div className="flex items-center gap-3">
                <Input type="file" accept=".jpg,.jpeg,.png,.webp,.pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="payment-screenshot-input" />
                {file && <span className="flex items-center gap-1 text-xs text-accent"><Upload className="h-3 w-3" /> {file.name}</span>}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="save-payment-button">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : isEdit ? "Save Changes" : "Record Payment"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
